import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_INTERVENTIONTYPES } from "../../../utils/constants";
import interventionTypeService from "../../../services/ticketService/interventionTypeService";
import { InterventionTypeInterface } from "../../../models/TicketModule/InterventionType";

interface UpdateInterventionTypesContext {
  previousInterventionTypes: InterventionTypeInterface[];
}

const useUpdateInterventionType = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    InterventionTypeInterface,
    Error,
    InterventionTypeInterface,
    UpdateInterventionTypesContext
  >({
    mutationFn: async (interventionType: InterventionTypeInterface) => {
      // Assuming the ID is stored in the interventionType object or can be derived
      const { id, ...interventionTypeData } = interventionType;
      return interventionTypeService.Update(id || "", interventionTypeData);
    },

    onMutate: async (updatedInterventionType: InterventionTypeInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousInterventionTypes =
        queryClient.getQueryData<InterventionTypeInterface[]>([
          CACHE_KEY_INTERVENTIONTYPES,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<InterventionTypeInterface[]>(
        [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (interventionTypes = []) =>
          interventionTypes.map((interventionType) =>
            interventionType.id === updatedInterventionType.id
              ? updatedInterventionType
              : interventionType
          )
      );

      return { previousInterventionTypes };
    },

    onSuccess: (savedInterventionType) => {
      queryClient.setQueryData<InterventionTypeInterface[]>(
        [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (interventionTypes) =>
          interventionTypes?.map((interventionType) =>
            interventionType.id === savedInterventionType.id
              ? savedInterventionType
              : interventionType
          )
      );
    },

    onError: (error, updatedInterventionType, context) => {
      if (!context) return;

      queryClient.setQueryData<InterventionTypeInterface[]>(
        [
          CACHE_KEY_INTERVENTIONTYPES,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousInterventionTypes
      );
    },
  });
};

export default useUpdateInterventionType;
